import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import DotGrid from './components/DotGrid';
import AnimatedText from './components/TypeWriter';
import GlassButton from './components/GlassButton';
import TechStackBar from './components/TechStackBar';
import ProjectGrid from './components/ProjectGrid';
import Resume from './pages/Resume';
import { siteConfig } from './config/siteConfig';
import javaIcon from './assets/tech/java.png';
import vueIcon from './assets/tech/Vue.png';
import springBootIcon from './assets/tech/springboot.png';
import springCloudIcon from './assets/tech/spring-cloud.png';
import tomcatIcon from './assets/tech/tomcat.png';
import linuxIcon from './assets/tech/linux.png';
import csharpIcon from './assets/tech/csharp.png';
import dotnetIcon from './assets/tech/net.png';
import redisIcon from './assets/tech/redis.png';
import rabbitMqIcon from './assets/tech/rabbitmq_.png';
import rocketMqIcon from './assets/tech/rocket-mq.png';
import mqttxIcon from './assets/tech/mqttx.png';
import mysqlIcon from './assets/tech/MySQL.png';
import sqliteIcon from './assets/tech/SQLite 2.png';
import sqlServerIcon from './assets/tech/sqlserver.png';
import dmDbIcon from './assets/tech/达梦数据库2.png';
import androidIcon from './assets/tech/Android.png';
import html5Icon from './assets/tech/HTML5.png';
import cssIcon from './assets/tech/css.png';
import echartsIcon from './assets/tech/echarts-copy.png';
import elementUiIcon from './assets/tech/element-ui_eleme.png';
import javascriptIcon from './assets/tech/javascript.png';
import wechatMiniProgramIcon from './assets/tech/微信小程序.png';
import reactIcon from './assets/tech/react.png';
import nodejsIcon from './assets/tech/node.js.png';
import dockerIcon from './assets/tech/Docker2.png';
import nginxIcon from './assets/tech/Nginx.png';
import mybatisIcon from './assets/tech/mybatis.png';
import './App.css';

function App() {
  const [showButtons, setShowButtons] = useState(false);
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [kbCopied, setKbCopied] = useState(false);
  const navigate = useNavigate();
  const [techBarHeight, setTechBarHeight] = useState(0);
  const [techBarCollapsed, setTechBarCollapsed] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty('--tech-stack-bar-height', `${techBarHeight}px`);
  }, [techBarHeight]);

  useEffect(() => {
    document.documentElement.style.setProperty('--tech-stack-bar-collapsed', techBarCollapsed ? '1' : '0');
  }, [techBarCollapsed]);

  const techStackGroups = [
    {
      title: '核心编程语言',
      items: [
        { name: 'Java', src: javaIcon },
        { name: 'C#', src: csharpIcon },
      ],
    },
    {
      title: '开发框架',
      columns: [
        {
          title: '后端',
          items: [
            { name: 'Spring Boot', src: springBootIcon },
            { name: 'Spring Cloud', src: springCloudIcon },
            { name: 'MyBatis', src: mybatisIcon },
          ],
        },
        {
          title: '前端',
          items: [
            { name: 'Vue', src: vueIcon },
            { name: 'React', src: reactIcon },
            { name: '.NET', src: dotnetIcon },
          ],
        },
      ],
    },
    {
      title: '中间件 / 服务器',
      items: [
        { name: 'MQTTX', src: mqttxIcon },
        { name: 'RocketMQ', src: rocketMqIcon },
        { name: 'RabbitMQ', src: rabbitMqIcon },
        { name: 'Docker', src: dockerIcon },
        { name: 'Nginx', src: nginxIcon },
        { name: 'Tomcat', src: tomcatIcon },
        { name: 'Linux', src: linuxIcon },
      ],
    },
    {
      title: '数据库 / 缓存',
      items: [
        { name: 'MySQL', src: mysqlIcon },
        { name: 'SQL Server', src: sqlServerIcon },
        { name: 'Redis', src: redisIcon },
        { name: 'SQLite', src: sqliteIcon },
        { name: '达梦数据库', src: dmDbIcon },
      ],
    },
    {
      title: '全栈 / 移动端拓展',
      items: [
        { name: 'HTML5', src: html5Icon },
        { name: 'JavaScript', src: javascriptIcon },
        { name: 'CSS', src: cssIcon },
        { name: 'Node.js', src: nodejsIcon },
        { name: 'ECharts', src: echartsIcon },
        { name: 'Element UI', src: elementUiIcon },
        { name: 'Android', src: androidIcon },
        { name: '微信小程序', src: wechatMiniProgramIcon },
      ],
    },
  ];

  const handleProfileClick = () => {
    console.log('个人简介按钮被点击');
    navigate('/resume');
  };

  const handleBlogClick = () => {
    console.log('我的博客按钮被点击');
    setShowKnowledgeBase(true);
  };

  const handlePersonalBlogClick = () => {
    console.log('个人博客按钮被点击');
    window.open(siteConfig.blog.url, '_blank', 'noopener,noreferrer');
  };

  const handleVibeCodingClick = () => {
    console.log('vibe coding 按钮被点击');
    setShowProjects(true);
  };

  const handleProjectClick = (project) => {
    console.log('项目点击:', project.name);
    // 使用 <a> 标签打开本地文件
    const link = document.createElement('a');
    link.href = project.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // 打开新页面
    // window.open(project.url, '_blank', 'noopener,noreferrer');
    setShowProjects(false);
  };

  const handleKnowledgeBaseConfirm = () => {
    window.open(siteConfig.knowledgeBase.url, '_blank', 'noopener,noreferrer');
    setShowKnowledgeBase(false);
  };

  const handleCopyAuthCode = async () => {
    const code = siteConfig.knowledgeBase.authCode;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setKbCopied(true);
      window.setTimeout(() => setKbCopied(false), 1200);
    } catch (e) {
      console.error('复制失败', e);
    }
  };

  const handleAnimationComplete = () => {
    setShowButtons(true);
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="App">
            <DotGrid
              dotSize={6}           // 点的大小
              gap={24}               // 点之间的间距
              baseColor="#2b2550"    // 默认颜色
              activeColor="#a855f7"  // 鼠标靠近时的高亮颜色
              proximity={220}        // 颜色渐变的影响范围
              pushRadius={200}       // 推开效果的范围
              pushStrength={0.5}     // 推开的力度
              returnSpeed={0.05}    // 回弹速度（越小弹性越强）
              rippleRadius={300}     // 涟漪扩散范围
              rippleStrength={7}     // 涟漪强度
              rippleDuration={500}  // 涟漪持续时间（毫秒）
              style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}
            />
            <TechStackBar
              groups={techStackGroups}
              iconBasePath="/assets/tech"
              onHeightChange={setTechBarHeight}
              onToggle={({ isMobile, expanded }) => {
                setTechBarCollapsed(Boolean(isMobile && !expanded));
              }}
            />
            <div className="content">
              <AnimatedText 
                title="Hi, I'm QuentinYuan！"
                subtitle="Quentin的目标：争取将代码写的和诗一样优雅~"
                titleSpeed={30}
                subtitleSpeed={15}
                titleDelay={200}
                subtitleDelay={150}
                onAnimationComplete={handleAnimationComplete}
              />
              {showButtons && (
                <div className="glass-buttons-container">
                  <GlassButton onClick={handleProfileClick}>
                    个人简介
                  </GlassButton>
                  <GlassButton onClick={handleBlogClick}>
                    个人知识库
                  </GlassButton>
                  <GlassButton onClick={handlePersonalBlogClick}>
                    个人博客
                  </GlassButton>
                  <GlassButton onClick={handleVibeCodingClick}>
                    vibe coding
                  </GlassButton>
                </div>
              )}
            </div>

            {showKnowledgeBase && (
              <div
                role="dialog"
                aria-modal="true"
                className="kb-modal__overlay"
                onClick={() => setShowKnowledgeBase(false)}
              >
                <div className="kb-modal__panel" onClick={(e) => e.stopPropagation()}>
                  <div className="kb-modal__header">
                    <div className="kb-modal__title">知识库访客授权码</div>
                    <button
                      type="button"
                      className="kb-modal__close"
                      onClick={() => setShowKnowledgeBase(false)}
                      aria-label="关闭"
                      title="关闭"
                    >
                      ×
                    </button>
                  </div>
                  <div className="kb-modal__body kb-modal__body--confirm">
                    <div className="kb-modal__message">
                      <div className="kb-modal__code-label">授权码</div>
                      <div className="kb-modal__code-row">
                        <div className="kb-modal__code" aria-label="授权码">
                          {siteConfig.knowledgeBase.authCode}
                        </div>
                        <button
                          type="button"
                          className="kb-modal__copy"
                          onClick={handleCopyAuthCode}
                        >
                          {kbCopied ? '已复制' : '复制'}
                        </button>
                      </div>
                      <div className="kb-modal__hint">如果新窗口提示输入授权码，请粘贴/输入授权码完成验证。</div>
                    </div>
                    <div className="kb-modal__actions">
                      <button
                        type="button"
                        className="kb-modal__btn kb-modal__btn--ghost"
                        onClick={() => setShowKnowledgeBase(false)}
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        className="kb-modal__btn kb-modal__btn--primary"
                        onClick={handleKnowledgeBaseConfirm}
                      >
                        确认
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showProjects && (
              <ProjectGrid
                projects={siteConfig.projects}
                onClose={() => setShowProjects(false)}
                onProjectClick={handleProjectClick}
              />
            )}
          </div>
        }
      />
      <Route path="/resume" element={<Resume onBack={() => navigate('/')} />} />
    </Routes>
  );
}

export default App;



